import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantObjectRef, AssistantPendingDTO, AssistantUndoableDTO } from '@contracts/assistant';
import type {
  NudgeActionCardDTO, NudgeActionView, NudgeDTO, NudgeTrayDTO,
} from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import { api, ApiClientError } from '../../../api/client';
import { useNarrowView } from '../../../lib/media';
import { focusWorkplaceItem } from '../../workplace/focus-request';
import { useAssistant } from '../AssistantProvider';
import { actionPath } from '../api';
import { parseObjLinks } from '../markdown';
import { refreshFor } from '../refresh';
import { NUDGES_KEY, actNudge } from './api';

/** The JSON a tray card's confirm answers (no chat turn to resume, D116): what ran, and its undo group if it has one. */
type ConfirmDone = { actionId: Id; objects?: AssistantObjectRef[]; group?: AssistantUndoableDTO };

const sameCard = (a: NudgeActionCardDTO, b: NudgeActionCardDTO) => a.nudgeId === b.nudgeId && a.index === b.index;

/** The tray with one card put in (or replaced): the stream-free twin of the chat's `withCard`. */
export function withNudgeCard(tray: NudgeTrayDTO | undefined, card: NudgeActionCardDTO): NudgeTrayDTO | undefined {
  if (tray === undefined) return tray;
  const { nudgeId, index, pending, undoable } = card;
  const next: NudgeActionCardDTO = { nudgeId, index, ...(pending ? { pending } : {}), ...(undoable ? { undoable } : {}) };
  return { ...tray, actionCards: tray.actionCards.some((c) => sameCard(c, next))
    ? tray.actionCards.map((c) => (sameCard(c, next) ? next : c)) : [...tray.actionCards, next] };
}

/** A refusal that settles the click (a 4xx): the next click is a new request. A lost answer keeps the key (§7.5). */
const settles = (e: unknown): boolean => e instanceof ApiClientError && e.status >= 400 && e.status < 500;

/** The objects a nudge names: its text's chips, what its undo refreshes and glows. */
const chipsOf = (n: NudgeDTO): AssistantObjectRef[] => parseObjLinks(n.text)
  .flatMap(({ type, id }) => (type === 'work' ? [] : [{ type, id }]));
/** Every nudge capability writes tasks (reschedule, status, `work.nudge`): the Workplace and its readers go stale. */
const TASK_REF: AssistantObjectRef = { type: 'task', id: '' };

/**
 * A nudge's action chips and its cards (spec §7.5; D57, D61, D94). A capability click POSTs with one request key per
 * (nudge, index), kept on every retry until an answer settles it; its card goes into the tray at once and the tray is
 * read again. `open` goes to the object or the route (the phone sheet steps aside); `ask` hands the label to the panel,
 * which starts a new conversation with the nudge as context. Every refusal is told through `onRefused`.
 */
export function useNudgeActions({ onAsk, onRefused }: {
  onAsk: (text: string, nudgeId: Id) => void; onRefused: (e: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const narrow = useNarrowView();
  const { hanaUndo, setOpen } = useAssistant();
  const keys = useRef(new Map<string, Id>());
  const [running, setRunning] = useState<ReadonlySet<Id>>(() => new Set());
  const [acting, setActing] = useState<ReadonlySet<Id>>(() => new Set());
  /** Cards a forceConfirm click opened here: they mount on their Sửa form. */
  const [forced, setForced] = useState<ReadonlySet<Id>>(() => new Set());
  /**
   * Cards saved from their Sửa form. The server answers an edit under a new action id, so the card remounts; one listed
   * here mounts on Xác nhận, not on its form again (S9 G7).
   */
  const [edited, setEdited] = useState<ReadonlySet<Id>>(() => new Set());

  const mark = (set: typeof setRunning, id: Id, on: boolean) => set((s) => {
    const next = new Set(s);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const reread = useCallback(() => queryClient.invalidateQueries({ queryKey: NUDGES_KEY }), [queryClient]);

  const open = (a: NudgeActionView) => {
    if (narrow) setOpen(false);
    if (a.route !== undefined) { void navigate(a.route); return; }
    const t = a.target;
    if (t === undefined) return;
    if (t.type === 'task' || t.type === 'note') {
      focusWorkplaceItem(t.id, { moveFocus: narrow }, (to, opts) => { void navigate(to, opts); });
    } else if (t.type === 'project') {
      void navigate(`/projects/${t.id}`);
    }
  };

  const run = async (n: NudgeDTO, a: NudgeActionView) => {
    const k = `${n.id}:${a.index}`;
    const requestId = keys.current.get(k) ?? crypto.randomUUID();
    keys.current.set(k, requestId);
    mark(setRunning, n.id, true);
    try {
      const result = await actNudge(n.id, a.index, requestId);
      keys.current.delete(k);
      if (a.forceConfirm === true && result.pending !== undefined) {
        const id = result.pending.actionId;
        setForced((s) => new Set(s).add(id));
      }
      queryClient.setQueryData<NudgeTrayDTO>(NUDGES_KEY, (t) => withNudgeCard(t, result));
      if (result.undoable !== undefined) {
        const chips = chipsOf(n);
        hanaUndo.offer(result.undoable, chips);
        void refreshFor(queryClient, [TASK_REF, ...chips]);       // refreshFor reads the types only
      }
    } catch (e) {
      if (settles(e)) keys.current.delete(k);
      onRefused(e);
    } finally {
      mark(setRunning, n.id, false);
      void reread();
    }
  };

  const onAction = (n: NudgeDTO, a: NudgeActionView) => {
    if (a.kind === 'open') open(a);
    else if (a.kind === 'ask') onAsk(a.label, n.id);
    else if (!running.has(n.id)) void run(n, a);
  };

  /** Xác nhận / Từ chối on a tray card: JSON, since a proactive card has no chat turn to resume. */
  const onAct = (actionId: Id, kind: 'confirm' | 'reject') => {
    if (acting.has(actionId)) return;
    mark(setActing, actionId, true);
    void (async () => {
      try {
        const done = await api<ConfirmDone>(actionPath(actionId, kind), { method: 'POST', body: {} });
        if (kind === 'confirm') {
          const objects = done.objects ?? [];
          if (done.group !== undefined) hanaUndo.offer(done.group, objects);
          void refreshFor(queryClient, objects);
        }
      } catch (e) {
        onRefused(e);
      } finally {
        mark(setActing, actionId, false);
        void reread();
      }
    })();
  };

  /** Lưu on a tray card: the new action takes the old one's place at once, then the tray is read again. */
  const onEdited = (card: NudgeActionCardDTO, next: AssistantPendingDTO) => {
    setEdited((s) => new Set(s).add(next.actionId));
    queryClient.setQueryData<NudgeTrayDTO>(NUDGES_KEY, (t) => withNudgeCard(t, { nudgeId: card.nudgeId,
      index: card.index, pending: next }));
    void reread();
  };

  return { onAction, onAct, onEdited, running, acting, forced, edited, reread };
}
